import React from "react";
import PropTypes from "prop-types";
import Button from "../../atoms/Button";
import Avatar from "../../atoms/Avatar";
import classNames from "../../../utils/classNames";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import "./index.scss";

/**
 * WorkspaceHeaderPanel
 * Compact, reusable panel that surfaces the workspace summary and actions.
 * Intended to be shown as an info panel under the sidebar user-spaces trigger.
 */
export default function WorkspaceHeaderPanel({
  title,
  description,
  iconUrl,
  imageAlt,
  memberCount,
  visibility,
  role,
  canManageMembers,
  canEditImage,
  isImageUpdating,
  imageEditLabel,
  imageUpdatingLabel,
  isLeaving,
  isDeleting,
  isVisibilityUpdating,
  deleteActionLabel,
  deleteActionProcessingLabel,
  onEditImage,
  onLeaveWorkspace,
  onRequestDeleteWorkspace,
  onToggleVisibility,
  onOpenMembers,
  onOpenInfo,
  className,
}) {
  const isOwner = role === "OWNER";
  const normalizedIconUrl = typeof iconUrl === "string" && iconUrl.trim()
    ? resolveMediaUrl(iconUrl)
    : null;
  const initials = title
    ? String(title)
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("")
    : "•";

  return (
    <section className={classNames("workspace-info-panel", className)} aria-label="Workspace summary">
      <div className="workspace-info-panel__body">
        <div className="workspace-info-panel__avatar-shell">
          <Avatar
            src={normalizedIconUrl || undefined}
            alt={imageAlt || "Workspace image"}
            size={72}
            radius={16}
            className="workspace-info-panel__avatar"
            fallback={initials || "•"}
          />
          {canEditImage && (
            <button
              type="button"
              className="workspace-info-panel__avatar-edit"
              onClick={onEditImage}
              disabled={isImageUpdating}
              aria-label={isImageUpdating ? imageUpdatingLabel : imageEditLabel}
            >
              {isImageUpdating ? imageUpdatingLabel : imageEditLabel}
            </button>
          )}
        </div>
        <div className="workspace-info-panel__content">
          <h2 className="workspace-info-panel__title">{title}</h2>
          <div className="workspace-info-panel__subtitle">
            {description && <span className="workspace-info-panel__desc">{description}</span>}
            {typeof memberCount === "number" && (
              <span className="workspace-info-panel__pill" aria-label="Members">
                {Number(memberCount).toLocaleString()} members
              </span>
            )}
            {visibility && <span className="workspace-info-panel__pill">{visibility}</span>}
          </div>
        </div>
        <div className="workspace-info-panel__actions">
          {role !== "OWNER" && (
            <Button
              text={isLeaving ? "Leaving…" : "Leave band"}
              variant="secondary"
              className="workspace-info-panel__btn workspace-info-panel__btn--leave"
              onClick={onLeaveWorkspace}
              disabled={isLeaving}
            />
          )}
          {isOwner && onRequestDeleteWorkspace && (
            <Button
              text={isDeleting ? deleteActionProcessingLabel : deleteActionLabel}
              variant="danger"
              className="workspace-info-panel__btn workspace-info-panel__btn--delete"
              onClick={onRequestDeleteWorkspace}
              disabled={isDeleting}
            />
          )}
          {canManageMembers && (
            <Button
              text={isVisibilityUpdating ? "Updating…" : visibility === "PUBLIC" ? "Make private" : "Make public"}
              variant="secondary"
              className="workspace-info-panel__btn"
              onClick={onToggleVisibility}
              disabled={isVisibilityUpdating}
            />
          )}
          <Button
            text={canManageMembers ? "Manage members" : "View members"}
            variant="secondary"
            className="workspace-info-panel__btn"
            onClick={onOpenMembers}
          />
          <Button
            text="View info"
            variant="secondary"
            className="workspace-info-panel__btn"
            onClick={onOpenInfo}
          />
        </div>
      </div>
    </section>
  );
}

WorkspaceHeaderPanel.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  iconUrl: PropTypes.string,
  imageAlt: PropTypes.string,
  memberCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  visibility: PropTypes.string,
  role: PropTypes.string,
  canManageMembers: PropTypes.bool,
  canEditImage: PropTypes.bool,
  isImageUpdating: PropTypes.bool,
  imageEditLabel: PropTypes.string,
  imageUpdatingLabel: PropTypes.string,
  isLeaving: PropTypes.bool,
  isDeleting: PropTypes.bool,
  isVisibilityUpdating: PropTypes.bool,
  deleteActionLabel: PropTypes.string,
  deleteActionProcessingLabel: PropTypes.string,
  onEditImage: PropTypes.func,
  onLeaveWorkspace: PropTypes.func,
  onRequestDeleteWorkspace: PropTypes.func,
  onToggleVisibility: PropTypes.func,
  onOpenMembers: PropTypes.func,
  onOpenInfo: PropTypes.func,
  className: PropTypes.string,
};

WorkspaceHeaderPanel.defaultProps = {
  title: "",
  description: "",
  iconUrl: "",
  imageAlt: "Workspace image",
  memberCount: undefined,
  visibility: undefined,
  role: undefined,
  canManageMembers: false,
  canEditImage: false,
  isImageUpdating: false,
  imageEditLabel: "Edit image",
  imageUpdatingLabel: "Updating…",
  isLeaving: false,
  isDeleting: false,
  isVisibilityUpdating: false,
  deleteActionLabel: "Delete workspace",
  deleteActionProcessingLabel: "Deleting workspace…",
  onEditImage: undefined,
  onLeaveWorkspace: undefined,
  onRequestDeleteWorkspace: undefined,
  onToggleVisibility: undefined,
  onOpenMembers: undefined,
  onOpenInfo: undefined,
  className: "",
};
